// WP-07 — ApiClient (LLD-05 §4): single typed facade over Axios. Features
// never touch HTTP directly. Responsibilities:
//   - attachAuthHeader: Bearer access token on every request
//   - single-flight JWT refresh on 401: concurrent 401s share ONE refresh,
//     queued requests replay with the new token, refresh failure clears the
//     session (LLD-05 §11)
//   - mapError: every failure leaves as AppError (BusinessError/UnexpectedError)
// The SPA talks ONLY to the Core API (LLD-05 §2.5); the tenant is derived
// server-side from the JWT `tid` — no tenant id ever travels in a request body.
import axios, {
  AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from "axios";
import type { LoginResultDto } from "./contracts";
import { mapError } from "./errors";
import { TokenStore, toTokens } from "./token-store";
import { sessionStore } from "../auth/session-store";

/** Endpoints that must never trigger the refresh interceptor. */
const AUTH_PATHS = ["/auth/login", "/auth/refresh", "/auth/revoke"];

interface RetriableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

function isAuthPath(url: string | undefined): boolean {
  return url !== undefined && AUTH_PATHS.some((path) => url.startsWith(path));
}

export class ApiClient {
  private readonly http: AxiosInstance;
  private readonly tokens: TokenStore;
  /** Single-flight refresh: all concurrent 401s await the same promise. */
  private refreshPromise: Promise<string> | null = null;

  constructor(baseURL: string = import.meta.env.VITE_API_BASE_URL ?? "/api") {
    this.tokens = new TokenStore();
    this.http = axios.create({ baseURL, timeout: 30_000 });
    this.http.interceptors.request.use((config) =>
      this.attachAuthHeader(config),
    );
    this.http.interceptors.response.use(
      (response) => response,
      (error: AxiosError) => this.onResponseError(error),
    );
  }

  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    return (await this.http.get<T>(url, { params })).data;
  }

  async post<T>(url: string, body?: unknown): Promise<T> {
    return (await this.http.post<T>(url, body)).data;
  }

  async patch<T>(url: string, body?: unknown): Promise<T> {
    return (await this.http.patch<T>(url, body)).data;
  }

  async put<T>(url: string, body?: unknown): Promise<T> {
    return (await this.http.put<T>(url, body)).data;
  }

  async delete<T>(url: string): Promise<T> {
    return (await this.http.delete<T>(url)).data;
  }

  private attachAuthHeader(
    config: InternalAxiosRequestConfig,
  ): InternalAxiosRequestConfig {
    const tokens = this.tokens.get();
    if (tokens && !isAuthPath(config.url)) {
      config.headers.set("Authorization", `Bearer ${tokens.accessToken}`);
    }
    return config;
  }

  private async onResponseError(error: AxiosError): Promise<unknown> {
    const config = error.config as RetriableConfig | undefined;
    const status = error.response?.status;

    if (
      status === 401 &&
      config &&
      !config._retried &&
      !isAuthPath(config.url)
    ) {
      config._retried = true;
      let accessToken: string;
      try {
        accessToken = await this.refreshAccessToken();
      } catch {
        // Refresh failed (rotated away, revoked, org suspended): clear the
        // session — RequireAuth bounces to sign-in (LLD-05 §11).
        sessionStore.getState().clearSession();
        throw mapError(error);
      }
      config.headers.set("Authorization", `Bearer ${accessToken}`);
      return this.http.request(config);
    }

    throw mapError(error);
  }

  private refreshAccessToken(): Promise<string> {
    this.refreshPromise ??= this.doRefresh().finally(() => {
      this.refreshPromise = null;
    });
    return this.refreshPromise;
  }

  private async doRefresh(): Promise<string> {
    const current = this.tokens.get();
    if (!current) throw new Error("no refresh token");
    // Goes through this.http but AUTH_PATHS exempts it from the interceptor,
    // so a 401 here rejects straight out (no recursion).
    const result = await this.post<LoginResultDto>("/auth/refresh", {
      refreshToken: current.refreshToken,
    });
    this.tokens.set(toTokens(result));
    return result.accessToken;
  }
}

/** App-wide singleton (LLD-05 §4 «singleton»). Tests construct their own. */
export const apiClient = new ApiClient();
